import { styles } from '~/modules/identification/forms/BusinessRelations/styles.ts';
import { Grid } from '@mui/material';
import TableCell from '~/components/ListCardTable/TableCell.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.tsx';
import { IBAN_MAX_LENGTH } from '~/constants/common.ts';
import { TableActionButtons } from '~/components/ListCardTable/TableActionButtons.tsx';
import { TAccounts } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { EMPTY_ACCOUNTS } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import { Path, useFormContext } from 'react-hook-form';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { infoByIban } from '~/store/identification-form/thunks/thunks.ts';

interface IAccountTableProps {
  index: number;
  parentIndex?: number;
  section?: string;
  disabled?: boolean;
  shownTableAction?: boolean;
  append?: (item: TAccounts) => void;
  remove?: (index: number) => void;
}

export const AccountTable = ({
  index,
  parentIndex,
  section,
  disabled = true,
  append,
  remove,
  shownTableAction,
}: IAccountTableProps) => {
  const dispatch = useAppDispatch();
  const { getValues, setValue, trigger } = useFormContext<TAccounts>();
  const field = section
    ? `${section}.accounts.${index}`
    : `additionalInformation.${parentIndex}.accounts.${index}`;
  const fieldIBAN = `${field}.iban` as Path<TAccounts>;
  const fieldBankMFO = `${field}.bankMFO` as Path<TAccounts>;

  const handleIBAN = async () => {
    const value = getValues(fieldIBAN);

    if (typeof value === 'string' && value.length === IBAN_MAX_LENGTH) {
      const normalized = value.slice(0, 2).toUpperCase() + value.slice(2);

      if (normalized !== value) {
        setValue(fieldIBAN, normalized, {
          shouldValidate: true,
        });
      }

      try {
        const iban = await dispatch(infoByIban({ data: normalized })).unwrap();
        setValue(fieldBankMFO, iban.bankMFO, { shouldValidate: true });
      } catch {
        setValue(fieldBankMFO, null);
        trigger(fieldIBAN);
      }
    }
  };

  const handleIBANBlur = () => {
    const value = getValues(fieldIBAN);
    if (typeof value === 'string' && value.length >= 2) {
      const normalized = value.slice(0, 2).toUpperCase() + value.slice(2);
      setValue(fieldIBAN, normalized, { shouldValidate: true });
    }
  };

  const handleAdd = () => {
    if (!append) return;
    append(EMPTY_ACCOUNTS);
  };

  const handleRemove = () => {
    if (!remove) return;
    remove(index);
  };

  return (
    <Grid container sx={styles.row}>
      <TableCell title="Номер рахунку" size={6}>
        <FormInput
          name={`${field}.iban`}
          placeholder={'Введіть рахунок у форматі IBAN'}
          variant="standard"
          fullWidth
          disabled={disabled}
          onChange={handleIBAN}
          sx={{ ...styles.tableInput, pt: '14px' }}
          inputProps={{
            onBlur: handleIBANBlur,
            minLength: 1,
            maxLength: IBAN_MAX_LENGTH,
          }}
        />
      </TableCell>
      <TableCell title="Дата відкриття рахунку" size={5}>
        <FormDateInput
          name={`${field}.openDate`}
          variant="standard"
          isCurrentMaxDate
          disabled={disabled}
          placeholder="Введіть дату відкриття рахунку"
          sx={{ mt: '14px', '& .MuiInputBase-input': { pl: 2 } }}
        />
      </TableCell>
      {shownTableAction && (
        <TableActionButtons addClick={handleAdd} removeClick={handleRemove} showRemove />
      )}
    </Grid>
  );
};
